import type Transition from '@ember/routing/transition';
import EmberService, { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { redirect } from 'webapp/utils/common';

export const GENIE_THREAD_ID_PARAM = 'genieThreadId';
export const GENIE_FROM_SPACE_ID_PARAM = 'genieFromSpaceId';

const STORAGE_KEY = 'genie-thread-agency-return';

export const RETURN_TARGET_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

interface ReturnTarget {
	sourceThreadId: string;
	agencySpaceId: number;
	capturedAt: number;
}

export default class GenieThreadAgencyReturnService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	captureFromUrl(): void {
		this.pruneExpiredTarget();

		const params = new URLSearchParams(window.location.search);
		if (!params.has(GENIE_THREAD_ID_PARAM) && !params.has(GENIE_FROM_SPACE_ID_PARAM)) {
			return;
		}

		const sourceThreadId = params.get(GENIE_THREAD_ID_PARAM);
		const agencySpaceId = Number(params.get(GENIE_FROM_SPACE_ID_PARAM));
		if (sourceThreadId && agencySpaceId) {
			this.storeTarget({ sourceThreadId, agencySpaceId });
		}

		params.delete(GENIE_THREAD_ID_PARAM);
		params.delete(GENIE_FROM_SPACE_ID_PARAM);
		const query = params.toString();
		const cleanUrl =
			window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
		try {
			window.history.replaceState(window.history.state, '', cleanUrl);
		} catch {
			// history blocked — leftover query params are harmless.
		}
	}

	threadIdFromTransition(transition: Transition): string | null {
		return (transition.to?.params as { thread_id?: string } | undefined)?.thread_id ?? null;
	}

	showErrorIfForeignSourceThread(threadId: string | null, errorMessage: string): boolean {
		if (!this.isForeignSourceThread(threadId)) {
			return false;
		}
		this.infolayerService.errorWithActionButton(errorMessage, {
			text: 'Go to thread',
			onClick: () => this.returnToThread(),
		});
		return true;
	}

	private isForeignSourceThread(threadId: string | null): boolean {
		const target = this.target;
		return (
			!!target &&
			!!threadId &&
			target.sourceThreadId === threadId &&
			this.accountService.id !== target.agencySpaceId
		);
	}

	private get target(): ReturnTarget | null {
		const target = this.readStoredTarget();
		return target && !this.isExpired(target) ? target : null;
	}

	private readStoredTarget(): ReturnTarget | null {
		try {
			const raw = sessionStorage.getItem(STORAGE_KEY);
			return raw ? (JSON.parse(raw) as ReturnTarget) : null;
		} catch {
			return null;
		}
	}

	private isExpired(target: ReturnTarget): boolean {
		return !target.capturedAt || Date.now() - target.capturedAt > RETURN_TARGET_MAX_AGE_MS;
	}

	private pruneExpiredTarget(): void {
		const target = this.readStoredTarget();
		if (target && this.isExpired(target)) {
			this.clearTarget();
		}
	}

	private storeTarget(target: Omit<ReturnTarget, 'capturedAt'>): void {
		try {
			sessionStorage.setItem(
				STORAGE_KEY,
				JSON.stringify({ ...target, capturedAt: Date.now() }),
			);
		} catch {
			// storage blocked — degrade to "no return offered" instead of breaking boot.
		}
	}

	private clearTarget(): void {
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch {
			// storage blocked — nothing to clear.
		}
	}

	private returnToThread(): void {
		const target = this.target;
		if (!target) {
			return;
		}
		this.clearTarget();
		const path = `/genie/thread/${target.sourceThreadId}`;
		redirect(`/change/space/${target.agencySpaceId}?redirect_path=${encodeURIComponent(path)}`);
	}
}

declare module '@ember/service' {
	interface Registry {
		'genie-thread-agency-return': GenieThreadAgencyReturnService;
	}
}
